import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/useAuthStore';
import { useAuthModalStore } from '../../stores/useAuthModalStore';
import { ticketService } from '../../services/ticketService';
import { toPng } from 'html-to-image';

interface FeedbackModalProps {
    isOpen: boolean;
    onClose: () => void;
}

type FeedbackCategory = 'suggestion' | 'bug' | null;
type ScreenshotMode = 'idle' | 'capturing' | 'annotating';

const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose }) => {
    const { isAuthenticated, user } = useAuthStore();
    const { openModal: openAuthModal } = useAuthModalStore();
    const modalRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const navigate = useNavigate();

    const handleGoToTickets = () => {
        onClose();
        navigate('/tickets');
    };

    // State
    const [selectedCategory, setSelectedCategory] = useState<FeedbackCategory>(null);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [screenshot, setScreenshot] = useState<string | null>(null);
    const [screenshotMode, setScreenshotMode] = useState<ScreenshotMode>('idle');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    // Drawing state
    const [isDrawing, setIsDrawing] = useState(false);
    const [drawColor, setDrawColor] = useState('#ff0000');
    const [originalImage, setOriginalImage] = useState<HTMLImageElement | null>(null);

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen && screenshotMode === 'idle') {
            setTimeout(() => {
                setSelectedCategory(null);
                setTitle('');
                setDescription('');
                setScreenshot(null);
                setSuccess(false);
                setError('');
                setOriginalImage(null);
            }, 300);
        }
    }, [isOpen, screenshotMode]);

    // Close on escape (only when not in annotating mode)
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                if (screenshotMode === 'annotating') {
                    // Cancel annotation
                    setScreenshotMode('idle');
                    setScreenshot(null);
                    setOriginalImage(null);
                } else if (screenshotMode === 'idle') {
                    onClose();
                }
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [onClose, screenshotMode]);

    const handleCategorySelect = (category: FeedbackCategory) => {
        setSelectedCategory(category);
        setError('');
    };

    const handleBack = () => {
        setSelectedCategory(null);
        setError('');
    };

    const handleCaptureScreen = async () => {
        try {
            setScreenshotMode('capturing');

            // Wait for modal to hide
            await new Promise(resolve => setTimeout(resolve, 300));

            // Capture using html-to-image which handles modern CSS/layout better
            const dataUrl = await toPng(document.body, {
                quality: 0.95,
                backgroundColor: '#111827', // Dark background default
                filter: (node) => {
                    // Ignore the capture overlay
                    if (node.id === 'feedback-capture-overlay') return false;

                    // Ignore avatar images that are causing CORS/Load errors (w-10 h-10 rounded-full)
                    if (node instanceof HTMLElement &&
                        node.tagName === 'IMG' &&
                        node.classList.contains('rounded-full') &&
                        node.classList.contains('w-10')) {
                        return false;
                    }

                    return true;
                },
                fetchRequestInit: {
                    cache: 'force-cache',
                },
                imagePlaceholder: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+P+/HgAFhAJ/wlseKgAAAABJRU5ErkJggg==',
            });

            setScreenshot(dataUrl);

            // Load image for canvas drawing
            const img = new Image();
            img.src = dataUrl;
            img.onload = () => {
                setOriginalImage(img);
                setScreenshotMode('annotating');
            };
        } catch (err: any) {
            console.error('Screen capture failed:', err);
            setError('Falha ao capturar tela. Tente novamente.');
            setScreenshotMode('idle');
        }
    };

    // Initialize canvas when entering annotation mode
    useEffect(() => {
        if (screenshotMode === 'annotating' && canvasRef.current && originalImage) {
            const canvas = canvasRef.current;
            canvas.width = originalImage.width;
            canvas.height = originalImage.height;
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.drawImage(originalImage, 0, 0);
            }
        }
    }, [screenshotMode, originalImage]);

    const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        setIsDrawing(true);
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        const x = (e.clientX - rect.left) * scaleX;
        const y = (e.clientY - rect.top) * scaleY;

        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.beginPath();
            ctx.moveTo(x, y);
            ctx.strokeStyle = drawColor;
            ctx.lineWidth = 4;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
        }
    };

    const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (!isDrawing || !canvasRef.current) return;

        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        const x = (e.clientX - rect.left) * scaleX;
        const y = (e.clientY - rect.top) * scaleY;

        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.lineTo(x, y);
            ctx.stroke();
        }
    };

    const stopDrawing = () => {
        setIsDrawing(false);
    };

    const clearDrawing = () => {
        if (canvasRef.current && originalImage) {
            const ctx = canvasRef.current.getContext('2d');
            if (ctx) {
                ctx.drawImage(originalImage, 0, 0);
            }
        }
    };

    const confirmScreenshot = () => {
        if (canvasRef.current) {
            const dataUrl = canvasRef.current.toDataURL('image/png');
            setScreenshot(dataUrl);
        }
        setScreenshotMode('idle');
    };

    const cancelScreenshot = () => {
        setScreenshot(null);
        setOriginalImage(null);
        setScreenshotMode('idle');
    };

    const handleRemoveScreenshot = () => {
        setScreenshot(null);
        setOriginalImage(null);
    };

    const handleSubmit = async () => {
        // Check auth
        if (!isAuthenticated) {
            onClose();
            openAuthModal({
                pendingAction: () => {
                    // Re-open feedback after login
                },
            });
            return;
        }

        if (!title.trim()) {
            setError('Por favor, adicione um título.');
            return;
        }

        if (!description.trim()) {
            setError('Por favor, descreva sua solicitação.');
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            const ticketType = selectedCategory === 'bug' ? 'complaint' : 'suggestion';

            await ticketService.createTicket({
                subject: title,
                message: description,
                type: ticketType,
                priority: selectedCategory === 'bug' ? 'high' : 'medium',
                screenshot: screenshot || null,
            });

            setSuccess(true);

            // Redirect to tickets after 2 seconds
            setTimeout(() => {
                onClose();
                navigate('/tickets');
            }, 2000);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Erro ao enviar. Tente novamente.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Annotation overlay (full screen)
    if (screenshotMode === 'annotating') {
        return (
            <div className="fixed inset-0 z-[9999] bg-black">
                {/* Toolbar */}
                <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-snapfy-inner rounded-xl shadow-2xl px-4 py-3 flex items-center gap-4 z-[10000] border border-snapfy-border">
                    <span className="text-snapfy-text-primary text-sm font-medium">Desenhe para destacar</span>
                    <div className="w-px h-6 bg-snapfy-inner" />

                    {/* Color picker */}
                    <div className="flex items-center gap-2">
                        {['#ff0000', '#00ff00', '#0066ff', '#ffff00', '#ff00ff'].map(color => (
                            <button
                                key={color}
                                onClick={() => setDrawColor(color)}
                                className={`w-6 h-6 rounded-full border-2 transition-transform ${drawColor === color ? 'scale-125 border-white' : 'border-snapfy-border'
                                    }`}
                                style={{ backgroundColor: color }}
                            />
                        ))}
                    </div>

                    <div className="w-px h-6 bg-snapfy-inner" />

                    <button
                        onClick={clearDrawing}
                        className="text-snapfy-text-secondary hover:text-snapfy-text-primary text-sm flex items-center gap-1"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Limpar
                    </button>

                    <div className="w-px h-6 bg-snapfy-inner" />

                    <button
                        onClick={cancelScreenshot}
                        className="px-3 py-1.5 text-snapfy-text-secondary hover:text-snapfy-text-primary text-sm transition-colors"
                    >
                        Cancelar
                    </button>

                    <button
                        onClick={confirmScreenshot}
                        className="px-4 py-1.5 snapfy-gradient text-white rounded-xl text-sm font-medium hover:opacity-90 transition-opacity"
                    >
                        Confirmar
                    </button>
                </div>

                {/* Canvas */}
                <div className="w-full h-full overflow-auto flex items-center justify-center p-4">
                    <canvas
                        ref={canvasRef}
                        className="max-w-full max-h-full cursor-crosshair shadow-2xl rounded-xl"
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                    />
                </div>

                {/* Instructions */}
                <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-snapfy-inner/90 rounded-xl px-4 py-2 text-snapfy-text-secondary text-sm">
                    Clique e arraste para desenhar • ESC para cancelar
                </div>
            </div>
        );
    }

    // Capturing overlay
    if (screenshotMode === 'capturing') {
        return (
            <div id="feedback-capture-overlay" className="fixed inset-0 z-[9999] bg-transparent pointer-events-none">
                <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-snapfy-inner/90 rounded-xl px-4 py-2 text-snapfy-text-primary animate-pulse">
                    Capturando tela...
                </div>
            </div>
        );
    }

    // Normal modal
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div
                ref={modalRef}
                className="bg-snapfy-surface rounded-2xl w-full max-w-md shadow-2xl border border-snapfy-border overflow-hidden"
            >
                {/* Header */}
                <div className="p-6 border-b border-snapfy-border">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            {selectedCategory && (
                                <button
                                    onClick={handleBack}
                                    className="text-snapfy-text-secondary hover:text-snapfy-text-primary transition-colors"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                    </svg>
                                </button>
                            )}
                            <h2 className="text-xl font-bold text-snapfy-text-primary">
                                {success ? 'Enviado!' : selectedCategory ? (selectedCategory === 'bug' ? 'Reportar Bug' : 'Enviar Sugestão') : 'Como podemos ajudar?'}
                            </h2>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-snapfy-text-secondary hover:text-snapfy-text-primary transition-colors"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    {success ? (
                        <div className="text-center py-8">
                            <div className="w-16 h-16 bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <h3 className="text-snapfy-text-primary font-semibold text-lg mb-2">Obrigado pelo feedback!</h3>
                            <p className="text-snapfy-text-secondary">Sua mensagem foi enviada com sucesso.</p>
                        </div>
                    ) : !selectedCategory ? (
                        <>
                            <div className="space-y-4">
                                <button
                                    onClick={() => handleCategorySelect('suggestion')}
                                    className="w-full p-4 bg-snapfy-surface-hover/50 hover:bg-snapfy-inner border border-snapfy-border rounded-xl flex items-center gap-4 transition-colors group"
                                >
                                    <div className="w-12 h-12 bg-[#1a0a3e]/30 rounded-xl flex items-center justify-center">
                                        <svg className="w-6 h-6 text-snapfy-accent-soft" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                        </svg>
                                    </div>
                                    <div className="text-left">
                                        <h3 className="text-snapfy-text-primary font-semibold group-hover:text-snapfy-accent-soft transition-colors">Sugestão</h3>
                                        <p className="text-snapfy-text-secondary text-sm">Compartilhe ideias de melhorias</p>
                                    </div>
                                </button>

                                <button
                                    onClick={() => handleCategorySelect('bug')}
                                    className="w-full p-4 bg-snapfy-surface-hover/50 hover:bg-snapfy-inner border border-snapfy-border rounded-xl flex items-center gap-4 transition-colors group"
                                >
                                    <div className="w-12 h-12 bg-red-900/30 rounded-xl flex items-center justify-center">
                                        <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                    </div>
                                    <div className="text-left">
                                        <h3 className="text-snapfy-text-primary font-semibold group-hover:text-red-400 transition-colors">Reportar Bug</h3>
                                        <p className="text-snapfy-text-secondary text-sm">Algo não está funcionando?</p>
                                    </div>
                                </button>
                            </div>
                            <div className="pt-4 border-t border-snapfy-border text-center mt-4">
                                <button onClick={handleGoToTickets} className="text-snapfy-accent-soft text-sm hover:text-snapfy-accent-soft transition-colors flex items-center justify-center gap-1 mx-auto group">
                                    <span className="underline-offset-4 group-hover:underline">Acompanhar meus tickets existentes</span>
                                    <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="space-y-4">
                            {error && (
                                <div className="p-3 bg-red-900/20 border border-red-500 rounded-xl text-red-400 text-sm">
                                    {error}
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-snapfy-text-secondary mb-2">Título</label>
                                <input
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder={selectedCategory === 'bug' ? 'Ex: Erro ao salvar imagem' : 'Ex: Adicionar filtro de cores'}
                                    className="w-full px-4 py-3 bg-snapfy-inner border border-snapfy-border rounded-xl text-snapfy-text-primary placeholder-snapfy-text-secondary focus:ring-2 focus:ring-[#7063f7] focus:outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-snapfy-text-secondary mb-2">Descrição</label>
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder={selectedCategory === 'bug' ? 'Descreva o que aconteceu e como reproduzir...' : 'Descreva sua ideia em detalhes...'}
                                    rows={4}
                                    className="w-full px-4 py-3 bg-snapfy-inner border border-snapfy-border rounded-xl text-snapfy-text-primary placeholder-snapfy-text-secondary focus:ring-2 focus:ring-[#7063f7] focus:outline-none resize-none"
                                />
                            </div>

                            {/* Screenshot Section */}
                            <div>
                                <label className="block text-sm font-medium text-snapfy-text-secondary mb-2">Screenshot (opcional)</label>
                                {screenshot ? (
                                    <div className="relative">
                                        <img
                                            src={screenshot}
                                            alt="Screenshot"
                                            className="w-full h-32 object-cover rounded-xl border border-snapfy-border"
                                        />
                                        <button
                                            onClick={handleRemoveScreenshot}
                                            className="absolute top-2 right-2 w-8 h-8 bg-red-600 hover:bg-red-500 rounded-full flex items-center justify-center text-white transition-colors"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                        <button
                                            onClick={handleCaptureScreen}
                                            className="absolute bottom-2 right-2 px-3 py-1 bg-snapfy-inner/80 hover:bg-snapfy-inner text-snapfy-text-secondary text-xs rounded-xl flex items-center gap-1 transition-colors"
                                        >
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                            </svg>
                                            Recapturar
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        onClick={handleCaptureScreen}
                                        className="w-full p-4 border-2 border-dashed border-snapfy-border rounded-xl text-snapfy-text-secondary hover:border-[#7063f7] hover:text-snapfy-accent-soft transition-colors flex items-center justify-center gap-2"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        Capturar tela
                                    </button>
                                )}
                            </div>

                            <button
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                                className="w-full py-3 snapfy-gradient text-white font-semibold rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {isSubmitting ? (
                                    <>
                                        <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Enviando...
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                        </svg>
                                        Enviar
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FeedbackModal;
